import { CategoryAxis, ValueAxis } from "@/components/tool-kit/charts/axes";
import { ChartSvg, ChartTitles } from "@/components/tool-kit/charts/chart-svg";
import type { ChartStyle } from "@/components/tool-kit/charts/controls";
import { Marker, px } from "@/components/tool-kit/charts/marks";
import {
  colorIndex,
  paletteColor,
  textColorOn,
} from "@/lib/tool-kit/charts/color";
import {
  type NumberFormat,
  percentText,
  tickFormat,
  valueText,
} from "@/lib/tool-kit/charts/format";
import {
  CHART_FONT_SIZE,
  CHART_HEIGHT,
  CHART_PADDING,
  CHART_WIDTH,
  cartesianFrame,
  heightForLabels,
  LINE_HEIGHT,
  layoutCategoryLabels,
  rotatedOverflow,
  TICK_GAP,
  TICK_SIZE,
} from "@/lib/tool-kit/charts/layout";
import { bandScale, linearScale, niceTicks } from "@/lib/tool-kit/charts/scale";
import { maxTextWidth } from "@/lib/tool-kit/charts/text";
import type { ParetoRow } from "@/lib/tool-kit/color-charts/pareto";
import { CHART_INK } from "@/lib/tool-kit/palette";

/*
 * A Pareto chart: sorted bars on the left axis, the running total as a line on the right.
 *
 * The left axis runs from 0 to the total, not to the tallest bar, so that the right axis
 * (0–100%) lines up with it: the point where the line reaches 100% is the top of the
 * plot, and a bar's height is its true share of the whole. That is the classic form, and
 * it makes the 80% reference line a straight horizontal one.
 */

const TICK = CHART_FONT_SIZE.tick;
const VALUE = CHART_FONT_SIZE.value;
const AXIS_BAND = CHART_FONT_SIZE.axisTitle * LINE_HEIGHT + 8;
const MUTED_BAR = "#bdbdbd";
const BAND = { paddingInner: 0.1, paddingOuter: 0.06 };
const HALO = {
  stroke: CHART_INK.background,
  strokeWidth: 3,
  paintOrder: "stroke",
};
const PERCENT_STEPS = [0, 0.2, 0.4, 0.6, 0.8, 1];

function percentTick(fmt: NumberFormat, value: number): string {
  return fmt(value, { style: "percent", maximumFractionDigits: 0 });
}

export function ParetoChart({
  id,
  rows,
  total,
  vitalFew,
  shade,
  showValues,
  style,
  fmt,
  label,
  description,
  cumulativeTitle,
  thresholdText,
}: {
  id: string;
  rows: ParetoRow[];
  total: number;
  /** How many leading rows reach the 80% line. */
  vitalFew: number;
  /** Color those rows and grey the rest. */
  shade: boolean;
  showValues: boolean;
  /** `yTitle` names the count axis, `xTitle` the categories. */
  style: ChartStyle;
  fmt: NumberFormat;
  label: string;
  description: string;
  /** Title of the right-hand axis. */
  cumulativeTitle: string;
  /** The words beside the dashed 80% line. */
  thresholdText: string;
}) {
  const axis = niceTicks(0, total, { exact: true, integer: false });
  const tick = (value: number) =>
    value === total && !axis.ticks.includes(value)
      ? valueText(fmt, value)
      : tickFormat(fmt, axis.decimals)(value);
  const rightTicks = PERCENT_STEPS.map((step) => percentTick(fmt, step));
  const rightLabelWidth = maxTextWidth(rightTicks, { fontSize: TICK });
  const rightTitle = cumulativeTitle.trim();
  const base = {
    width: CHART_WIDTH,
    height: CHART_HEIGHT,
    title: style.apa ? "" : style.title,
    xTitle: style.xTitle,
    yTitle: style.yTitle,
    leftLabelWidth: maxTextWidth([...axis.ticks, total].map(tick), {
      fontSize: TICK,
    }),
    rightPadding:
      rightLabelWidth + TICK_SIZE + TICK_GAP + (rightTitle ? AXIS_BAND : 0),
    topPadding: 20,
  };
  const probe = cartesianFrame({ ...base, bottomLabelHeight: 0 });
  const step = bandScale(rows.length, [0, probe.plot.width], BAND).step;
  const labels = layoutCategoryLabels(
    rows.map((row) => row.label),
    step
  );
  const sized = {
    ...base,
    height: heightForLabels(labels.height),
    bottomLabelHeight: labels.height,
  };
  const firstCenter = step * (BAND.paddingOuter + (1 - BAND.paddingInner) / 2);
  const overflow = rotatedOverflow(cartesianFrame(sized), labels, firstCenter);
  const frame = cartesianFrame({
    ...sized,
    leftLabelWidth: sized.leftLabelWidth + overflow,
  });
  const { plot } = frame;
  const bottom = plot.y + plot.height;
  const right = plot.x + plot.width;
  const band = bandScale(rows.length, [plot.x, right], BAND);
  const y = linearScale([axis.min, axis.max], [bottom, plot.y]);
  const yShare = (share: number) => bottom - share * plot.height;
  // The total is the top of the plot and the 100% point, so label it when no tick does.
  const lastTick = axis.ticks.at(-1) ?? 0;
  const leftTicks =
    ((total - lastTick) / total) * plot.height >= 14
      ? [...axis.ticks, total]
      : axis.ticks;
  const start = colorIndex(style.color);
  const barColor = paletteColor(start, 0);
  const lineColor = paletteColor(start, 1);
  const points = rows.map((row, index) => ({
    x: band.center(index),
    y: yShare(row.cumulative),
  }));
  const path = points
    .map(
      (point, index) =>
        `${index === 0 ? "M" : "L"}${px(point.x)},${px(point.y)}`
    )
    .join("");

  return (
    <ChartSvg
      description={description}
      height={frame.height}
      id={id}
      label={label}
      width={frame.width}
    >
      <ChartTitles frame={frame} />
      {rightTitle ? (
        <text
          fill={CHART_INK.text}
          fontSize={CHART_FONT_SIZE.axisTitle}
          textAnchor="middle"
          transform={`rotate(90 ${px(CHART_WIDTH - CHART_PADDING - 4)} ${px(plot.y + plot.height / 2)})`}
          x={px(CHART_WIDTH - CHART_PADDING - 4)}
          y={px(plot.y + plot.height / 2)}
        >
          {rightTitle}
        </text>
      ) : null}
      <ValueAxis
        apa={style.apa}
        format={tick}
        grid={false}
        orient="left"
        plot={plot}
        scale={y}
        ticks={leftTicks}
        zeroLine
      />
      <g>
        <line
          stroke={style.apa ? CHART_INK.text : CHART_INK.axis}
          strokeWidth={1}
          x1={px(right)}
          x2={px(right)}
          y1={px(plot.y)}
          y2={px(bottom)}
        />
        {PERCENT_STEPS.map((share, index) => (
          <g key={share}>
            <line
              stroke={style.apa ? CHART_INK.text : CHART_INK.axis}
              strokeWidth={1}
              x1={px(right)}
              x2={px(right + TICK_SIZE)}
              y1={px(yShare(share))}
              y2={px(yShare(share))}
            />
            <text
              fill={style.apa ? CHART_INK.text : CHART_INK.muted}
              fontSize={TICK}
              x={px(right + TICK_SIZE + TICK_GAP)}
              y={px(yShare(share) + TICK * 0.35)}
            >
              {rightTicks[index]}
            </text>
          </g>
        ))}
      </g>
      <CategoryAxis
        apa={style.apa}
        labels={labels}
        orient="bottom"
        plot={plot}
        positions={rows.map((_, i) => band.center(i))}
      />
      {rows.map((row, index) => {
        const top = y(row.value);
        const fill = shade && index >= vitalFew ? MUTED_BAR : barColor;
        return (
          <rect
            fill={fill}
            height={px(Math.max(0, bottom - top))}
            key={`${row.label}-${index}`}
            width={px(band.bandwidth)}
            x={px(band.start(index))}
            y={px(top)}
          />
        );
      })}
      <line
        stroke={CHART_INK.muted}
        strokeDasharray="5 4"
        strokeWidth={1.25}
        x1={px(plot.x)}
        x2={px(right)}
        y1={px(yShare(0.8))}
        y2={px(yShare(0.8))}
      />
      <text
        fill={CHART_INK.muted}
        fontSize={VALUE}
        textAnchor="end"
        x={px(right - 4)}
        y={px(yShare(0.8) + VALUE + 3)}
        {...HALO}
      >
        {thresholdText}
      </text>
      <path
        d={path}
        fill="none"
        stroke={lineColor}
        strokeLinejoin="round"
        strokeWidth={2}
      />
      {points.map((point, index) => (
        <Marker
          fill={lineColor}
          key={`m-${rows[index].label}-${index}`}
          shape="circle"
          size={8}
          x={point.x}
          y={point.y}
        />
      ))}
      {showValues
        ? rows.map((row, index) => {
            const top = y(row.value);
            const height = bottom - top;
            const fill = shade && index >= vitalFew ? MUTED_BAR : barColor;
            const inside = height >= VALUE + 8;
            const text = valueText(fmt, row.value);
            return (
              <g key={`v-${row.label}-${index}`}>
                <text
                  fill={inside ? textColorOn(fill) : CHART_INK.text}
                  fontSize={VALUE}
                  textAnchor="middle"
                  x={px(band.center(index))}
                  y={px(inside ? top + VALUE + 3 : top - 4)}
                  {...(inside ? {} : HALO)}
                >
                  {text}
                </text>
                <text
                  fill={CHART_INK.text}
                  fontSize={VALUE}
                  fontWeight="bold"
                  textAnchor="end"
                  x={px(points[index].x + 5)}
                  y={px(Math.max(plot.y + VALUE, points[index].y - 9))}
                  {...HALO}
                >
                  {percentText(fmt, row.cumulative)}
                </text>
              </g>
            );
          })
        : null}
    </ChartSvg>
  );
}
